import React from 'react';
import { motion } from 'framer-motion';
import { FiClock, FiStar, FiUsers, FiMapPin, FiCheck, FiArrowRight } from 'react-icons/fi';
import SEOPageLayout from '../components/SEOPageLayout';
import FadeInView from '../components/FadeInView';
import { SITE_URL } from '../config/seo-data';

const ease = [0.22, 1, 0.36, 1];

const useCases = [
  { icon: FiMapPin, title: 'Villa Guests', desc: 'On-demand transport without renting a car. Your driver adapts to your daily plans and knows every shortcut.' },
  { icon: FiStar, title: 'Cruise Passengers', desc: 'Personalised shore excursion at your own pace. We guarantee your return before ship departure.' },
  { icon: FiUsers, title: 'Families', desc: 'Skip the stress of mountain roads and parking. Child seats included, comfort guaranteed.' },
  { icon: FiClock, title: 'Business Travel', desc: 'Professional transport between meetings and events. Punctual, discreet, and reliable.' },
];

const advantages = [
  { title: 'No parking stress', desc: 'Corfu Town parking is notoriously scarce, your driver drops and collects you.' },
  { title: 'Local knowledge', desc: 'Hidden beaches, the best tavernas, shortcuts avoiding summer traffic.' },
  { title: 'Mountain roads handled', desc: 'West coast and Pantokrator roads are narrow and winding, let a local drive.' },
  { title: 'No rental hassle', desc: 'No fuel costs, no insurance, no GPS needed, no unfamiliar roads.' },
  { title: 'Door-to-door flexibility', desc: 'Change plans on the fly. Stop where you want, stay as long as you like.' },
  { title: 'Available all day', desc: 'Half-day, full-day, or custom durations to match your schedule.' },
];

const HourlyChauffeur = () => (
  <SEOPageLayout
    title="Lasmari Chauffeurs | Corfu Hourly Chauffeur Service"
    description="Hire a private chauffeur in Corfu by the hour. Mercedes-Benz vehicles, professional drivers, flexible scheduling. Perfect for sightseeing, shopping, and events."
    keywords="corfu hourly chauffeur, corfu chauffeur service, hire driver corfu, corfu private driver, corfu luxury chauffeur, corfu VIP chauffeur"
    canonical={`${SITE_URL}/hourly-chauffeur`}
  >
    {/* Hero */}
    <section className="relative min-h-[85vh] flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('/images/backgrounds/chauffeur-liston-hero.webp')`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/40 to-black/80" />
        <div
          className="absolute inset-0"
          style={{ background: 'radial-gradient(ellipse 65% 55% at 50% 45%, rgba(0,0,0,0.55), transparent 72%)' }}
        />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center py-20">
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease }}
          className="text-white/85 text-xs uppercase tracking-[0.4em] mb-6 font-light"
          style={{ textShadow: '0 1px 3px rgba(0,0,0,0.85), 0 2px 16px rgba(0,0,0,0.6)' }}
        >
          Your Driver · Your Schedule
        </motion.p>

        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease, delay: 0.1 }}
          className="text-5xl md:text-6xl lg:text-7xl text-white mb-8 leading-[1.08] tracking-tight"
          style={{ fontFamily: "'Playfair Display', serif", textShadow: '0 1px 3px rgba(0,0,0,0.85), 0 2px 18px rgba(0,0,0,0.6)' }}
        >
          Private
          <br />
          <span className="font-light italic">Chauffeur</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease, delay: 0.2 }}
          className="text-white/80 text-lg lg:text-xl max-w-2xl mx-auto mb-10 font-light leading-relaxed"
          style={{ textShadow: '0 1px 3px rgba(0,0,0,0.85), 0 2px 16px rgba(0,0,0,0.6)' }}
        >
          Explore Corfu in luxury with a private driver on demand. Stop wherever you want, go at your own pace, in a premium Mercedes-Benz.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease, delay: 0.3 }}
        >
          <a
            href="https://wa.me/447404560313?text=Hello!%20I%27d%20like%20to%20inquire%20about%20your%20chauffeur%20service."
            target="_blank"
            rel="noopener noreferrer"
            className="bg-[#ba1609] text-white px-10 py-4 rounded-full font-medium hover:bg-[#8e1107] transition-all duration-300 text-base tracking-wide"
          >
            Request a Quote
          </a>
        </motion.div>
      </div>
    </section>

    {/* Who Uses */}
    <section className="py-24 lg:py-32 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <FadeInView
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease }}
          className="text-center mb-16"
        >
          <p className="text-[#ba1609] text-xs uppercase tracking-[0.4em] mb-4">Perfect For</p>
          <h2 className="text-3xl lg:text-5xl font-light text-[#1a1012]" style={{ fontFamily: "'Playfair Display', serif" }}>
            Who Uses Our <span className="italic">Chauffeur</span>
          </h2>
        </FadeInView>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {useCases.map((u, i) => (
            <FadeInView
              key={u.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: i * 0.1, ease }}
              className="group bg-[#faf7f6] border border-black/10 rounded-2xl p-8 shadow-sm hover:shadow-lg hover:border-[#ba1609]/30 transition-[color,background-color,border-color,box-shadow] duration-500 text-center"
            >
              <div className="w-14 h-14 rounded-xl bg-[#fbeceb] border border-[#ba1609]/20 flex items-center justify-center mx-auto mb-6 group-hover:bg-[#ba1609]/15 transition-colors duration-500">
                <u.icon className="w-6 h-6 text-[#ba1609]" />
              </div>
              <h3 className="text-[#1a1012] text-lg font-medium mb-3">{u.title}</h3>
              <p className="text-[#5b4d4f] text-sm leading-relaxed font-light">{u.desc}</p>
            </FadeInView>
          ))}
        </div>
      </div>
    </section>

    {/* Why Chauffeur vs Car Rental */}
    <section className="py-24 lg:py-32 bg-[#faf7f6]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-20 items-start">
          <FadeInView
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease }}
          >
            <p className="text-[#ba1609] text-xs uppercase tracking-[0.4em] mb-4">The Smart Choice</p>
            <h2 className="text-3xl lg:text-4xl font-light text-[#1a1012] mb-8" style={{ fontFamily: "'Playfair Display', serif" }}>
              Why a Chauffeur
              <br /><span className="italic">Instead of Rental?</span>
            </h2>
            <div className="space-y-4 text-[#5b4d4f] text-sm leading-relaxed font-light">
              <p>Many visitors find hiring a private driver more practical than renting a car in Corfu. The island's roads can be narrow and winding, especially on the west coast. Parking in Corfu Town is extremely limited.</p>
              <p>Our drivers know every shortcut, hidden beach, and excellent restaurant, letting you relax and enjoy rather than navigate.</p>
              <p>Available for half-day, full-day, or custom durations. Contact us for a personalised quote.</p>
            </div>
          </FadeInView>

          <FadeInView
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease }}
          >
            <div className="space-y-4">
              {advantages.map((adv, i) => (
                <FadeInView
                  key={adv.title}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: i * 0.06, ease }}
                  className="group bg-white border border-black/10 rounded-xl px-5 py-4 shadow-sm hover:shadow-md hover:border-[#ba1609]/30 transition-[color,background-color,border-color,box-shadow] duration-300"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-5 h-5 mt-0.5 rounded-full bg-[#fbeceb] border border-[#ba1609]/20 flex items-center justify-center shrink-0">
                      <FiCheck className="w-3 h-3 text-[#ba1609]" />
                    </div>
                    <div>
                      <h4 className="text-[#1a1012] text-sm font-medium mb-0.5">{adv.title}</h4>
                      <p className="text-[#5b4d4f] text-xs font-light">{adv.desc}</p>
                    </div>
                  </div>
                </FadeInView>
              ))}
            </div>
          </FadeInView>
        </div>
      </div>
    </section>

    {/* Bottom CTA */}
    <section className="py-24 bg-[#ba1609]">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <FadeInView
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease }}
        >
          <p className="text-white/85 text-xs uppercase tracking-[0.4em] mb-4">Ready?</p>
          <h2 className="text-3xl lg:text-5xl font-light text-white mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
            Your Driver <span className="italic">Awaits</span>
          </h2>
          <p className="text-white/85 text-sm font-light mb-10 max-w-lg mx-auto leading-relaxed">
            Tell us your dates and plans. We'll put together a personalised chauffeur package tailored to your stay.
          </p>
          <a
            href="https://wa.me/447404560313?text=Hello!%20I%27d%20like%20to%20inquire%20about%20your%20chauffeur%20service."
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-white text-[#ba1609] px-10 py-4 rounded-full font-medium hover:bg-[#fbeceb] transition-all duration-300 text-base tracking-wide"
          >
            Get a Quote on WhatsApp
          </a>
        </FadeInView>
      </div>
    </section>
  </SEOPageLayout>
);

export default HourlyChauffeur;
